import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/font_family.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/draggable.min.js';
import 'froala-editor/js/plugins/import_from_word.min.js';
import 'froala-editor/js/plugins/page_break.min.js';
import 'froala-editor/js/plugins/word_paste.min.js';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import DOMPurify from 'dompurify';
import FroalaEditor, { AlignType, FroalaEvents, FroalaOptions } from 'froala-editor';
import { of } from 'rxjs';
import { removePageBreaks } from '@capital-access/common/utils';
import { FireflyModalService } from '../modal';
import { FireflyFroalaLicenseProvider, FireflyLocalizationService, isMac, isSafari } from '../utils';
import { FireflyHashMap } from '../utils/localization/firefly-localization.model';
import { alignmentIcons, buttonsToTriggerClearFormatting, fonts, icons } from './models/constants';
import { TextEditorValidation } from './models/text-editor-validation.model';
import { ValidationError } from './models/text-editor-validation-error.enum';

// Froala's default paste-cleaning pipeline (independent of the wordPaste plugin) recursively
// walks every parsed DOM node and can throw "Maximum call stack size exceeded" - uncaught,
// since it runs deep inside Froala's own internals before any of our event hooks fire - when
// pasted HTML is extremely deeply nested. This is common with Office copy/paste, which often
// wraps individual words/characters in many layers of nested <span> formatting runs. Content
// nested deeper than this is bypassed entirely (see the paste capture listener below) rather
// than handed to Froala at all.
const MAX_SAFE_PASTE_NESTING_DEPTH = 300;
// Cheap guard so the (relatively expensive) nesting-depth measurement only runs for pastes
// large enough to plausibly contain 300+ levels of nesting - skips the extra DOM parse/walk
// entirely for the vast majority of small, everyday pastes.
const MIN_HTML_LENGTH_FOR_DEPTH_CHECK = 5000;

@Component({
  selector: 'f-text-editor',
  templateUrl: './text-editor.component.html',
  imports: [FroalaEditorModule, FroalaViewModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FireflyTextEditorComponent implements OnInit, OnDestroy {
  @ViewChild('contextualMenu') contextualMenu!: ElementRef;

  validation = input<TextEditorValidation>();
  additionalButtons = input<string[][]>([]);
  editor = input<FormControl<string | null>>(new FormControl(''));
  placeholder = input<string>('');
  additionalEvents = input<Partial<FroalaEvents>>({});
  enableImportFromWord = input<boolean>();

  editorOptions!: Partial<FroalaOptions>;
  readonly localization = toSignal(
    inject(FireflyLocalizationService, { optional: true })?.getLocalization() ?? of({} as FireflyHashMap),
    { initialValue: {} as FireflyHashMap }
  );
  readonly requiredErrorMessage = computed(() => this.localization()['fieldIsRequired'] || 'This field is required');
  readonly urlPopupWebAddressMessage = computed(
    () => this.localization()['urlPopupWebAddressMessage'] || 'Web Address'
  );
  readonly urlPopupTextToDisplayMessage = computed(
    () => this.localization()['urlPopupTextToDisplayMessage'] || 'Text to Display'
  );
  readonly urlPopupWebAddressPlaceholderMessage = computed(
    () => this.localization()['urlPopupWebAddressPlaceholderMessage'] || 'Enter web address'
  );
  readonly urlPopupTextToDisplayPlaceholderMessage = computed(
    () => this.localization()['urlPopupTextToDisplayPlaceholderMessage'] || 'Enter text to display'
  );
  readonly colorPickerButtonMessage = computed(() => this.localization()['colorPickerButtonMessage'] || 'Apply');
  readonly colorPickerPlaceholderMessage = computed(
    () => this.localization()['colorPickerPlaceholderMessage'] || 'Enter HEX color'
  );
  readonly imageInsertTitleMessage = computed(
    () => this.localization()['imageInsertTitle'] || 'Drag and drop to upload file or'
  );
  readonly imageUploaderBrowseFilesMessage = computed(
    () => this.localization()['uploaderBrowseFiles'] || 'Browse Files'
  );
  readonly alignMessage = computed(() => this.localization()['align'] || 'Align');
  readonly alignLeftMessage = computed(() => this.localization()['alignLeft'] || 'Align Left');
  readonly alignCenterMessage = computed(() => this.localization()['alignCenter'] || 'Align Center');
  readonly alignRightMessage = computed(() => this.localization()['alignRight'] || 'Align Right');
  readonly alignJustifyMessage = computed(() => this.localization()['alignJustify'] || 'Align Justify');
  readonly wordPasteModalTitle = computed(() => this.localization()['wordPasteModalTitle'] || 'Keep the formatting?');
  readonly wordPasteModalBody = computed(
    () =>
      this.localization()['wordPasteModalBody'] ||
      'The pasted content will keep its original formatting from Microsoft Word'
  );
  readonly wordPasteModalKeep = computed(() => this.localization()['wordPasteModalKeep'] || 'Keep Formatting');
  readonly wordPasteModalClean = computed(() => this.localization()['wordPasteModalClean'] || 'Paste Without It');

  private renderer = inject(Renderer2);
  private licenseService = inject(FireflyFroalaLicenseProvider, { optional: true });
  private modalService = inject(FireflyModalService, { optional: true });
  private pasteCaptureEl: (HTMLElement & { _ffPasteCapture?: (e: ClipboardEvent) => void }) | null = null;

  ngOnDestroy(): void {
    if (this.pasteCaptureEl?._ffPasteCapture) {
      this.pasteCaptureEl.removeEventListener('paste', this.pasteCaptureEl._ffPasteCapture, true);
      delete this.pasteCaptureEl._ffPasteCapture;
      this.pasteCaptureEl = null;
    }
  }

  ngOnInit(): void {
    this.defineIcons(icons);
    this.addAlignmentButtons(this);

    if (this.enableImportFromWord()) {
      import('mammoth')
        .then(module => {
          const mammoth = module.default ?? module;
          (window as Window & { mammoth?: typeof mammoth }).mammoth = mammoth;
        })
        .catch(err => console.error('[text-editor] Failed to load mammoth:', err));
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;

    // Get Froala's default list of allowed attributes
    const defaultAllowedAttrs = FroalaEditor.DEFAULTS.htmlAllowedAttrs;
    // Add custom attributes to the default list
    const extendedAllowedAttrs = [...defaultAllowedAttrs, 'ses:no-track', 'ses:tags'];
    // Shared clipboard plain-text fallback used by both word-paste and regular-paste handlers
    let pasteClipboardFallbackText: string | null = null;
    // Captured in the capture phase; used by paste.before for RTF-based Word detection.
    let capturedClipboardHtml: string | null = null;
    let capturedHasRtf = false;
    // Guards paste.afterCleanup from consuming pasteClipboardFallbackText while the Word
    // paste modal flow owns it (doWordPaste reads it asynchronously via setTimeout).
    let wordPasteFlowActive = false;
    // Shared reset for the two clipboard capture variables above - used by every fallback
    // path once it has consumed (or decided not to use) the captured clipboard data.
    const clearCapturedPasteState = () => {
      pasteClipboardFallbackText = null;
      capturedClipboardHtml = null;
    };

    this.editorOptions = {
      key: this.license,
      toolbarSticky: false,
      toolbarButtons: [
        ['undo', 'redo'],
        ['bold', 'italic', 'underline'],
        ['backgroundColor'],
        ['fontFamily'],
        ['fontSize'],
        ['textColor'],
        ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify'],
        ['formatOLSimple', 'formatUL'],
        ['html'],
        ...(this.additionalButtons() || []),
        ['clearFormatting']
      ],
      tableEditButtons: [
        'tableHeader',
        'tableFooter',
        'tableRows',
        'tableColumns',
        'tableStyle',
        'tableCells',
        'tableCellBackground',
        '-',
        'tableCellVerticalAlign',
        'tableAlignLeft',
        'tableAlignCenter',
        'tableAlignRight',
        'tableAlignJustify',
        'tableRemove'
      ],
      imageEditButtons: [
        'imageReplace',
        'imageAlignLeft',
        'imageAlignCenter',
        'imageAlignRight',
        '-',
        'imageCaption',
        'imageDisplay',
        'imageRemove'
      ],
      events: {
        initialized: function () {
          // Capture clipboard data in the capture phase (before Froala) for paste fallbacks.
          // Guard against double-registration if `initialized` fires more than once.
          const el = this.el as HTMLElement & { _ffPasteCapture?: (e: ClipboardEvent) => void };
          const froalaForCapture = this as FroalaEditor;
          if (!el._ffPasteCapture) {
            el._ffPasteCapture = (e: ClipboardEvent) => {
              const cd = e.clipboardData;
              if (!cd) return; // Guard: clipboard data unavailable in this context
              pasteClipboardFallbackText = cd.getData('text/plain') ?? null;
              capturedClipboardHtml = cd.getData('text/html') || null;
              capturedHasRtf = cd.types ? Array.from(cd.types).includes('text/rtf') : false;

              // Bypass Froala's default paste handling entirely for pathologically deep
              // pasted HTML (see MAX_SAFE_PASTE_NESTING_DEPTH) - insert a safe plain-text
              // fallback ourselves instead of letting Froala's recursive cleanup crash.
              const nestingDepth =
                capturedClipboardHtml && capturedClipboardHtml.length > MIN_HTML_LENGTH_FOR_DEPTH_CHECK
                  ? component.getMaxNestingDepth(capturedClipboardHtml)
                  : 0;
              if (nestingDepth > MAX_SAFE_PASTE_NESTING_DEPTH) {
                e.preventDefault();
                e.stopImmediatePropagation();
                const htmlFallback = component.getPasteWithoutFormattingHtml(
                  pasteClipboardFallbackText,
                  capturedClipboardHtml
                );
                if (htmlFallback) {
                  froalaForCapture.html.insert(htmlFallback, true);
                }
                clearCapturedPasteState();
              }
            };
            el.addEventListener('paste', el._ffPasteCapture, true);
            component.pasteCaptureEl = el;
          }

          // Intercept Froala's word paste modal and replace with Firefly confirmation modal
          if (component.modalService) {
            type FroalaWithPlugins = FroalaEditor & {
              modals: { show: (id: string) => void };
              wordPaste: { clean: (keepFormatting: boolean) => void };
            };
            const froalaInstance = this as unknown as FroalaWithPlugins;
            const originalModalShow = froalaInstance.modals.show.bind(froalaInstance.modals);
            froalaInstance.modals.show = function (id: string) {
              if (id === 'word_paste') {
                // Blur before opening modal to avoid Angular CDK aria-hidden errors.
                (document.activeElement as HTMLElement | null)?.blur();

                const modal = component.modalService!.openConfirmation({
                  style: 'confirmation',
                  title: component.wordPasteModalTitle(),
                  body: component.wordPasteModalBody(),
                  actionText: component.wordPasteModalKeep(),
                  dismissText: component.wordPasteModalClean()
                });

                // Inserts sanitized "without formatting" HTML fallback (preserves structure like
                // tables/images while stripping style/class/font junk), or falls back to plain text.
                // Avoids Froala's recursive cleanup pipeline for deeply nested Office content.
                const insertWithoutFormattingFallback = (): boolean => {
                  const htmlFallback = component.getPasteWithoutFormattingHtml(
                    pasteClipboardFallbackText,
                    capturedClipboardHtml
                  );
                  if (!htmlFallback) return false;
                  froalaInstance.html.insert(htmlFallback, true);
                  return true;
                };

                // Use two-argument .then() to avoid errors from clean(true) triggering the .catch() path.
                const doWordPaste = (keepFormatting: boolean) => {
                  froalaInstance.events.focus();

                  // "Paste Without It": insert sanitized fallback directly instead of running
                  // wordPaste.clean(false). clean() still has to fully parse/sanitize the entire
                  // clipboard HTML (large tables, deeply nested formatting, etc.) even when discarding
                  // formatting, which can freeze the tab (and can throw "Maximum call stack size
                  // exceeded" on deeply nested content, e.g. large Excel tables, since it's a
                  // recursive JS walk). Never call wordPaste.clean(false) here -
                  // insertWithoutFormattingFallback now uses sanitized HTML first (keeping
                  // tables/images) and falls back to text, so this should only be a no-op in the
                  // rare case there's truly no clipboard content at all.
                  if (!keepFormatting) {
                    insertWithoutFormattingFallback();
                    clearCapturedPasteState();
                    wordPasteFlowActive = false;
                    return;
                  }

                  const htmlBefore = froalaInstance.html.get();
                  let wordPasteCleanFailed = false;
                  try {
                    froalaInstance.wordPaste.clean(true);
                  } catch (e) {
                    console.error('[text-editor] wordPaste.clean failed:', e);
                    wordPasteCleanFailed = true;
                  }
                  // Fall back to plain text if wordPaste.clean inserted nothing or threw
                  // (e.g. "Maximum call stack size exceeded" on deeply nested content).
                  setTimeout(() => {
                    if (wordPasteCleanFailed || froalaInstance.html.get() === htmlBefore) {
                      insertWithoutFormattingFallback();
                    }
                    clearCapturedPasteState();
                    wordPasteFlowActive = false;
                  }, 0);
                };

                // Mark the Word paste flow as active so paste.afterCleanup does not
                // consume pasteClipboardFallbackText before doWordPaste's deferred read.
                wordPasteFlowActive = true;
                // Defer to next animation frame so CDK removes aria-hidden before editor re-focus.
                modal.result.then(
                  () => requestAnimationFrame(() => doWordPaste(true)),
                  () => requestAnimationFrame(() => doWordPaste(false))
                );
                return;
              }
              originalModalShow(id);
            };
          }

          const toolbar = this.$tb[0] as HTMLElement;
          const fontSizeBtn = toolbar.querySelectorAll('button[data-cmd="fontSize"]');
          const fontSizeBtnSpan = toolbar.querySelectorAll('button[data-cmd="fontSize"] span');
          const buttons = toolbar.querySelectorAll('button.fr-command');
          const selection = this.selection.get() as Selection;
          const clearFormattingButton = toolbar.querySelector('[data-cmd="clearFormatting"]');

          fontSizeBtn.forEach(item => item.setAttribute('title', 'Size'));
          fontSizeBtnSpan.forEach(item => (item.textContent = 'Size'));
          component.replaceSVGWithIcons(alignmentIcons);

          Array.from(buttons)
            .filter(button => buttonsToTriggerClearFormatting.includes(button.getAttribute('data-cmd')!))
            .forEach(item =>
              component.renderer.listen(item, 'click', () => component.toggleClearFormattingButton(toolbar, selection))
            );

          component.renderer.setAttribute(clearFormattingButton, 'disabled', 'disabled');

          this.popups.onShow = () => {
            this.button.bulkRefresh = function () {
              // bypass Froala's logic to prevent losing the active states of toolbar buttons.
            };
          };

          // After table drag/drop Froala wraps empty cells in <p> blocks,
          // adding an extra visual empty line. Listen on the drop target (editorEl)
          const editorEl = this.el as HTMLElement;
          editorEl.addEventListener('drop', () => {
            editorEl.querySelectorAll('td, th').forEach(cell => {
              const hasContent = (cell.textContent || '').replace(/\u200B/g, '').trim().length > 0;
              if (!hasContent) {
                cell.innerHTML = '';
              }
            });
          });

          // Remove page break artifacts (label + marker) from existing editor content.
          component.removePageBreakMarkersFromEditor(this as unknown as FroalaEditor);
        },
        'popups.hide.backgroundColor.picker': function () {
          const selection = this.selection.get() as Selection;
          const toolbar = this.$tb[0] as HTMLElement;
          component.toggleClearFormattingButton(toolbar, selection);
        },
        'popups.hide.textColor.picker': function () {
          const selection = this.selection.get() as Selection;
          const toolbar = this.$tb[0] as HTMLElement;
          component.toggleClearFormattingButton(toolbar, selection);
        },
        'popups.show.table.edit': function () {
          const popup = this.popups.get('table.edit')[0];
          component.replaceSVGWithIcons(alignmentIcons, popup);
        },
        'popups.show.table.colors': function () {
          const popup = this.popups.get('table.colors')[0];
          component.setupColorPickerHandler(popup);
        },
        'popups.show.backgroundColor.picker': function () {
          const popup = this.popups.get('backgroundColor.picker')[0];
          component.setupColorPickerHandler(popup);
        },
        'popups.show.textColor.picker': function () {
          const popup = this.popups.get('textColor.picker')[0];
          component.setupColorPickerHandler(popup);
        },
        'popups.show.image.insert': function () {
          const popup = this.popups.get('image.insert')[0];
          component.setupImageUploader(popup);
        },
        'popups.show.image.edit': function () {
          const popup = this.popups.get('image.edit')[0];
          component.replaceSVGWithIcons(alignmentIcons, popup);
        },
        'popups.show.link.insert': function () {
          const popup = this.popups.get('link.insert')[0];

          const updateLabelWithStar = (selector: string, message: string) => {
            const labelElement = popup.querySelector(selector);
            const starElement = component.renderer.createElement('span') as HTMLElement;
            labelElement.textContent = message;
            component.renderer.addClass(starElement, 'text-danger');
            starElement.textContent = '*';
            component.renderer.appendChild(labelElement, starElement);

            return labelElement;
          };

          updateLabelWithStar('.fr-link-attr[name="href"] + label', component.urlPopupWebAddressMessage());
          updateLabelWithStar('.fr-link-attr[name="text"] + label', component.urlPopupTextToDisplayMessage());

          const updateInputPlaceholder = (selector: string, placeholderMessage: string) =>
            component.renderer.setAttribute(popup.querySelector(selector), 'placeholder', placeholderMessage);
          updateInputPlaceholder('.fr-link-attr[name="href"]', component.urlPopupWebAddressPlaceholderMessage());
          updateInputPlaceholder('.fr-link-attr[name="text"]', component.urlPopupTextToDisplayPlaceholderMessage());
        },
        blur: function () {
          component.editor().markAsTouched();

          if (!component.contextualMenu.nativeElement.children.length) return;
          const contextualMenu = component.contextualMenu?.nativeElement;
          const editor = this as unknown as FroalaEditor;
          if (contextualMenu.classList.contains('show')) component.hideContextualMenu(editor, contextualMenu);
        },
        focus: () => {
          setTimeout(() => this.editor().markAsUntouched(), 0);
        },
        mouseup: function (e: MouseEvent) {
          if (!component.contextualMenu.nativeElement.children.length) return;
          const contextualMenuElement = component.contextualMenu.nativeElement;
          const editor = this as unknown as FroalaEditor;
          const isRightClick = component.isRightMouseButton(e);

          // editor.el.oncontextmenu = () => false; doesn't work in Safari and MacOS, so we need to disable the browser context menu
          if (isSafari() || isMac()) editor.el.addEventListener('contextmenu', (e: MouseEvent) => e.preventDefault());

          // Only do the expensive range/rect math and link lookup when the contextual menu
          // can actually be shown (right-click). This used to run unconditionally on every
          // mouseup - including plain left clicks that just move the cursor in a table cell -
          // forcing multiple synchronous layout reflows (getBoundingClientRect) plus a full
          // editor.el.querySelectorAll('a') scan on every click. For editors holding a large
          // amount of pasted content this made every click in the editor (e.g. in a table)
          // freeze the tab.
          if (isRightClick) {
            const parentRect = editor.el.parentElement.parentElement.getBoundingClientRect();

            if (editor.selection.text().length) {
              const range = editor.selection.ranges(0) as Range;
              const newRange = document.createRange();
              newRange.setStart(range.startContainer, range.startOffset);
              newRange.setEnd(range.endContainer, range.endOffset);
              const rect = newRange.getBoundingClientRect();
              component.showContextualMenu(
                rect.left - parentRect.left,
                rect.top - parentRect.top,
                editor,
                contextualMenuElement
              );
            }

            // Use the actual click target instead of scanning every <a> in the editor.
            const clickedLink = (e.target as HTMLElement)?.closest?.('a');
            if (clickedLink) {
              const linkRect = clickedLink.getBoundingClientRect();
              component.showContextualMenu(
                linkRect.left - parentRect.left,
                linkRect.top - parentRect.top,
                editor,
                contextualMenuElement
              );
            }
          }

          requestAnimationFrame(() => {
            if (
              editor.selection.isCollapsed() &&
              (e.target! as HTMLElement).tagName !== 'A' &&
              contextualMenuElement.classList.contains('show')
            )
              component.hideContextualMenu(editor, contextualMenuElement);
          });
        },
        click: function () {
          const selection = this.selection.get() as Selection;
          const toolbar = this.$tb[0] as HTMLElement;
          component.toggleClearFormattingButton(toolbar, selection);
        },
        'image.beforeUpload': function (files: File[]) {
          if (files.length) {
            const reader = new FileReader();

            reader.onload = (event: ProgressEvent<FileReader>) => {
              const result = event.target?.result as string;
              this.image.insert(result, false, {}, this.image.get());
            };

            reader.readAsDataURL(files[0]);
          }

          this.popups.hideAll();
          return false; // Stop default upload
        },
        // Intercept pastes where RTF is present but Froala's Word-marker regex misses the content.
        // Manually triggers paste.wordPaste so our modals.show override opens the Firefly modal.
        'paste.before': function (): boolean {
          if (!capturedHasRtf || !capturedClipboardHtml) return true;
          if (/data-teams/i.test(capturedClipboardHtml)) return true;

          const wordMarkerRegex =
            /(MSFontService|class="?Mso|class='?Mso|class="?Xl|class='?Xl|class=Xl|style="[^"]*\bmso-|style='[^']*\bmso-|w:WordDocument|LibreOffice|class="?TextRun|class="?NormalTextRun|class="?EOP|OneNote\.File|caret-color:\s*rgb\(0,\s*0,\s*0\)|OutlineElement|OfficeHtml)/i;
          if (wordMarkerRegex.test(capturedClipboardHtml)) return true;

          const froala = this as FroalaEditor & { wordPaste?: { clean: (keepFormatting: boolean) => void } };
          if (!froala.wordPaste) return true;

          const htmlToProcess = capturedClipboardHtml;
          capturedClipboardHtml = null;
          capturedHasRtf = false;

          froala.events.trigger('paste.wordPaste', [htmlToProcess]);
          return false;
        },
        // Fallback for non-Word pastes that produce empty content after Froala cleanup.
        // Skip during Word paste flow: pasteClipboardFallbackText is owned by doWordPaste
        // (read asynchronously via setTimeout) and must not be consumed here first.
        'paste.afterCleanup': function (html: string) {
          if (wordPasteFlowActive) return removePageBreaks(html);
          // Use textContent to detect empty Froala output — avoids regex-based tag stripping
          // which CodeQL flags as incomplete multi-character sanitization (CWE-116).
          const div = document.createElement('div');
          div.innerHTML = html; // detached node; only textContent is read below
          const isEmpty = !html.trim() || !/\S/.test(div.textContent ?? '');
          if (isEmpty && (pasteClipboardFallbackText || capturedClipboardHtml)) {
            const htmlFallback = component.getPasteWithoutFormattingHtml(
              pasteClipboardFallbackText,
              capturedClipboardHtml
            );
            clearCapturedPasteState();
            if (!htmlFallback) return removePageBreaks(html);
            return htmlFallback;
          }
          clearCapturedPasteState();
          // Single-pass cleanup on the pasted HTML string only (no editor.html.set round-trip,
          // avoiding recursive contentChanged storms on large pastes).
          return removePageBreaks(html);
        },
        ...this.additionalEvents()
      },
      placeholderText: this.placeholder(),
      fontSize: ['8', '10', '12', '14', '18', '24', '36'],
      fontFamilyDefaultSelection: 'Arial',
      fontFamily: Object.fromEntries(fonts.sort((a, b) => a[1].localeCompare(b[1]))),
      fontFamilySelection: true,
      imageOutputSize: true,
      fontSizeSelection: true,
      fontSizeUnit: 'pt',
      paragraphFormatSelection: true,
      attribution: false,
      linkEditButtons: [],
      htmlAllowedAttrs: extendedAllowedAttrs,
      scrollableContainer: '.fr-box',
      useClasses: false,
      codeViewKeepActive: true,
      ...(this.enableImportFromWord()
        ? {
            importFromWordFileTypesAllowed: ['docx'],
            importFromWordMaxFileSize: 5 * 1024 * 1024,
            importFromWordEnableImportOnDrop: true
          }
        : {})
    };
  }

  get requiredAndInvalid(): boolean | undefined {
    return !(this.validation()?.error === ValidationError.Required)
      ? undefined
      : this.editor().errors?.required && this.editor().touched;
  }

  get isCustomError(): boolean {
    return this.validation()?.error === ValidationError.Custom && this.editor().touched;
  }

  private get license(): string {
    return this.licenseService?.getLicense() || '';
  }

  private isRightMouseButton(e: MouseEvent): boolean {
    return e.button === 2;
  }

  private showContextualMenu(x: number, y: number, editor: FroalaEditor, contextualMenu: HTMLElement): void {
    const lineHeight = 16;
    editor.el.oncontextmenu = () => false;
    this.renderer.setStyle(contextualMenu, 'transform', `translate(${x}px, ${y + lineHeight}px)`);
    this.renderer.addClass(contextualMenu, 'show');
  }

  private hideContextualMenu(editor: FroalaEditor, contextualMenu: HTMLElement): void {
    editor.el.oncontextmenu = null;
    this.renderer.removeClass(contextualMenu, 'show');
  }

  private setupColorPickerHandler(popup: HTMLElement): void {
    const colorPickerClearIcon = popup.querySelector('.fr-submit');
    const input = popup.querySelector('input');
    this.renderer.setProperty(colorPickerClearIcon, 'textContent', this.colorPickerButtonMessage());
    this.renderer.setAttribute(input, 'placeholder', this.colorPickerPlaceholderMessage());

    if (popup.querySelector(`.f-i-trash-bin`) === null) {
      const svgElement = popup.querySelector(`.fr-color-set .fr-svg`)!;
      const parentElement = svgElement.parentElement;
      const newIcon = this.renderer.createElement('i');
      this.renderer.addClass(newIcon, 'f-i');
      this.renderer.addClass(newIcon, 'f-i-trash-bin');
      this.renderer.appendChild(parentElement, newIcon);
    }
  }

  private addAlignmentButtons(component: this): void {
    const config: { type: string; icon: string; align: AlignType }[] = [
      { type: 'imageAlignLeft', icon: 'f-i-alignment', align: 'left' },
      { type: 'imageAlignRight', icon: 'f-i-right-alignment', align: 'right' },
      { type: 'imageAlignCenter', icon: 'f-i-center-alignment', align: 'center' },
      { type: 'tableAlignLeft', icon: 'f-i-alignment', align: 'left' },
      { type: 'tableAlignRight', icon: 'f-i-right-alignment', align: 'right' },
      { type: 'tableAlignCenter', icon: 'f-i-center-alignment', align: 'center' },
      { type: 'tableAlignJustify', icon: 'f-i-width-alignment', align: 'justify' }
    ];
    config.forEach(({ type, icon, align }) =>
      FroalaEditor.RegisterCommand(type, {
        title: this.alignMessage(),
        icon: `<i class="f-i f-i-xs ${icon}"></i>`,
        undo: true,
        focus: true,
        refreshAfterCallback: true,
        callback: function (button) {
          setTimeout(() => {
            // timeout to override froala active state style logic
            const el = document.querySelector(`button[data-cmd="${button}"]`);
            component.renderer.addClass(el, 'fr-active');
          }, 100);

          type.includes('image') ? this.image.align(align) : this.table.horizontalAlign(align);
        }
      })
    );
  }

  private setupImageUploader(popup: HTMLElement): void {
    const layer = popup.querySelector('.fr-image-upload-layer')!;
    const form = layer.querySelector('.fr-form')!;
    const el = `
    <p class="body-50">${this.imageInsertTitleMessage()}</p>
    <span class="link link-primary body-50">${this.imageUploaderBrowseFilesMessage()}</span>
    `;
    this.renderer.setProperty(layer, 'textContent', '');

    layer.innerHTML = `${el}${form.outerHTML}`;
  }

  private replaceSVGWithIcons(mappings: { [key: string]: string[] }, el?: HTMLElement): void {
    Object.entries(mappings).forEach(([cmd, iconClassList]) => {
      const selector = `button[data-cmd="${cmd}"]`;
      const svgElement = (el || document).querySelector(`${selector} .fr-svg`);

      if (svgElement) {
        const parentElement = svgElement.parentElement;
        this.renderer.removeChild(parentElement, svgElement);
        const newIcon = this.renderer.createElement('i');
        iconClassList.forEach(cls => this.renderer.addClass(newIcon, cls));
        this.renderer.appendChild(parentElement, newIcon);
      }
    });
  }

  private isTextFormatted(selection: Selection | null): boolean {
    if (selection && selection.rangeCount > 0 && selection.toString().trim() !== '') {
      const range = selection.getRangeAt(0);
      const commonAncestor = range.commonAncestorContainer;

      const containsDesiredTagsOrStyles = (node: HTMLElement): boolean => {
        const desiredTags = ['STRONG', 'EM', 'U'];
        const hasDesiredTag = desiredTags.includes(node.tagName);
        const inlineColor = node.style?.color;
        const inlineBackgroundColor = node.style?.backgroundColor;
        const inlineFontSize = node.style?.fontSize;
        const hasInlineStyles = Boolean(inlineColor || inlineBackgroundColor || inlineFontSize);

        return hasDesiredTag || hasInlineStyles;
      };

      let currentElement = (
        commonAncestor.nodeType === 3 ? commonAncestor.parentElement : commonAncestor
      ) as HTMLElement;

      while (currentElement) {
        if (containsDesiredTagsOrStyles(currentElement)) return true;

        currentElement = currentElement.parentElement!;
      }

      const fragment = range.cloneContents();
      const childNodes = Array.from(fragment.childNodes);

      for (const node of childNodes) if (containsDesiredTagsOrStyles(node as HTMLElement)) return true;
    }

    return false;
  }

  private toggleClearFormattingButton(toolbar: HTMLElement, selection: Selection): void {
    const clearFormattingButton = toolbar.querySelector('[data-cmd="clearFormatting"]');
    if (this.isTextFormatted(selection)) {
      this.renderer.removeAttribute(clearFormattingButton, 'disabled');
    } else {
      this.renderer.setAttribute(clearFormattingButton, 'disabled', 'disabled');
    }
  }

  private defineIcons(icons: { [key: string]: string }): void {
    FroalaEditor.DefineIconTemplate('common', '<i class="f-i f-i-xs f-i-[NAME]"></i>');

    Object.entries(icons).forEach(([key, value]) => {
      FroalaEditor.DefineIcon(key, { NAME: value, template: 'common' });
    });
  }

  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private getPreferredPlainText(plainText: string | null, html: string | null): string {
    const trimmed = (plainText ?? '').trim();
    if (!trimmed) return this.htmlToPlainText(html ?? '');

    const looksLikeOfficeMarkup = /<[^>]+>|<!--|\/\*\s*Font Definitions\s*\*\/|@font-face|mso-|OfficeHtml/i.test(
      trimmed
    );
    if (!looksLikeOfficeMarkup) return trimmed;

    const fromHtml = this.htmlToPlainText(html ?? '');
    if (fromHtml) return fromHtml;

    return trimmed
      .replace(/<!--[\s\S]*?-->/g, ' ')
      .replace(/\/\*[\s\S]*?\*\//g, ' ')
      .replace(/@font-face[\s\S]*?\}/gi, ' ')
      .replace(/mso-[\w-]+\s*:\s*[^;]+;?/gi, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private getPasteWithoutFormattingHtml(plainText: string | null, html: string | null): string {
    const sanitizedHtml = this.sanitizePasteWithoutFormattingHtml(html ?? '');
    if (sanitizedHtml) return sanitizedHtml;

    const text = this.getPreferredPlainText(plainText, html);
    if (!text) return '';

    const escaped = this.escapeHtml(text);
    return `<p>${escaped.replace(/\n\n+/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
  }

  // Blocks dangerous URL schemes (javascript:, data:, vbscript:, file:) from href/src attributes.
  // Strips whitespace first to defeat obfuscated schemes (e.g. "java\nscript:").
  // Data URIs are allowed only for `<img src>` and only for common raster image MIME types, since
  // pasted images are frequently embedded as base64 data URIs (data:image/svg+xml is still blocked,
  // as SVG can carry embedded scripts).
  private isUnsafeUrl(url: string, options: { allowDataImage?: boolean } = {}): boolean {
    const normalized = url.replace(/\s+/g, '').toLowerCase();
    if (!normalized) return false;

    if (options.allowDataImage && /^data:image\/(png|jpe?g|gif|webp|bmp);base64,/.test(normalized)) {
      return false;
    }

    return /^(javascript|data|vbscript|file):/.test(normalized);
  }

  private sanitizePasteWithoutFormattingHtml(html: string): string {
    if (!html.trim()) return '';

    try {
      const container = this.sanitizeAndParse(html);

      const allowedTags = new Set([
        'P',
        'BR',
        'DIV',
        'SPAN',
        'B',
        'STRONG',
        'I',
        'EM',
        'U',
        'S',
        'UL',
        'OL',
        'LI',
        'A',
        'IMG',
        'TABLE',
        'THEAD',
        'TBODY',
        'TFOOT',
        'TR',
        'TH',
        'TD',
        'H1',
        'H2',
        'H3',
        'H4',
        'H5',
        'H6',
        'BLOCKQUOTE',
        'PRE',
        'CODE'
      ]);

      const allowedAttrsByTag = new Map<string, Set<string>>([
        ['A', new Set(['href'])],
        ['IMG', new Set(['src', 'alt', 'title', 'width', 'height'])],
        ['TH', new Set(['colspan', 'rowspan'])],
        ['TD', new Set(['colspan', 'rowspan'])]
      ]);

      const stack: Element[] = Array.from(container.children);
      while (stack.length) {
        const el = stack.pop()!;

        for (const child of Array.from(el.children)) {
          stack.push(child);
        }

        if (!allowedTags.has(el.tagName)) {
          const parent = el.parentNode;
          if (!parent) continue;
          while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
          }
          parent.removeChild(el);
          continue;
        }

        const allowedAttrs = allowedAttrsByTag.get(el.tagName) ?? new Set<string>();
        for (const attr of Array.from(el.attributes)) {
          if (!allowedAttrs.has(attr.name.toLowerCase())) {
            el.removeAttribute(attr.name);
          }
        }

        if (el.tagName === 'A' && el.hasAttribute('href')) {
          const href = el.getAttribute('href') ?? '';
          if (this.isUnsafeUrl(href)) {
            el.removeAttribute('href');
          }
        }

        if (el.tagName === 'IMG') {
          const src = el.getAttribute('src') ?? '';
          if (!src || this.isUnsafeUrl(src, { allowDataImage: true })) {
            const parent = el.parentNode;
            parent?.removeChild(el);
            continue;
          }
        }
      }

      const textContent = (container.textContent ?? '').trim();
      const hasVisualContent =
        textContent.length > 0 || !!container.querySelector('img,table,tr,td,th,tbody,thead,tfoot');

      return hasVisualContent ? container.innerHTML.trim() : '';
    } catch (e) {
      console.error('[text-editor] sanitizePasteWithoutFormattingHtml failed:', e);
      return '';
    }
  }

  // Derives plain text from clipboard HTML using native (non-recursive) browser DOM parsing/
  // textContent instead of Froala's wordPaste.clean(), which walks the parsed HTML with JS
  // recursion and can throw "Maximum call stack size exceeded" on deeply nested/huge content
  // (e.g. large Excel/Word tables). Used only as a fallback when no text/plain clipboard data
  // was available.
  private htmlToPlainText(html: string): string {
    try {
      const container = this.sanitizeAndParse(html);

      container.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
      container
        .querySelectorAll('p, div, tr, li, h1, h2, h3, h4, h5, h6, table')
        .forEach(el => el.insertAdjacentText('afterend', '\n'));
      return (container.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
    } catch (e) {
      console.error('[text-editor] htmlToPlainText failed:', e);
      return '';
    }
  }

  // Shared cleanup for both plain-text extraction and HTML sanitization: strips tags that
  // never carry meaningful pasted content (style/script/head/meta/link/xml/title) plus HTML
  // comment nodes, which is where Office/Word CSS blocks (e.g. "@font-face", "mso-" rules)
  // normally live and would otherwise leak into the output as visible text.
  private stripNonContentArtifacts(container: HTMLElement): void {
    container.querySelectorAll('style, script, head, meta, link, xml, title').forEach(el => el.remove());

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_COMMENT);
    const comments: Comment[] = [];
    while (walker.nextNode()) comments.push(walker.currentNode as Comment);
    comments.forEach(comment => comment.remove());
  }

  // Runs DOMPurify once and strips non-content artifacts into a fresh container.
  // All three HTML consumers (sanitizePasteWithoutFormattingHtml, htmlToPlainText,
  // getMaxNestingDepth) call this instead of duplicating the DOMPurify + createElement boilerplate.
  private sanitizeAndParse(html: string): HTMLElement {
    const container = document.createElement('div');
    container.innerHTML = DOMPurify.sanitize(html, { RETURN_DOM: false, RETURN_DOM_FRAGMENT: false });
    this.stripNonContentArtifacts(container);
    return container;
  }

  // Measures max DOM nesting depth using an explicit array as a stack rather than JS
  // recursion, so this itself can never stack-overflow no matter how deeply nested the
  // input HTML is. Used to detect pathological pasted content before handing it to Froala.
  private getMaxNestingDepth(html: string): number {
    try {
      const container = this.sanitizeAndParse(html);
      let maxDepth = 0;
      const stack: Array<{ node: Element; depth: number }> = [{ node: container, depth: 0 }];
      while (stack.length) {
        const { node, depth } = stack.pop()!;
        if (depth > maxDepth) maxDepth = depth;
        for (const child of Array.from(node.children)) {
          stack.push({ node: child, depth: depth + 1 });
        }
      }
      return maxDepth;
    } catch (e) {
      console.error('[text-editor] getMaxNestingDepth failed:', e);
      return 0;
    }
  }

  private removePageBreakMarkersFromEditor(editor: FroalaEditor): void {
    const currentHtml = editor.html.get();
    // Cheap pre-check avoids the cost of the full regex pass and DOM round-trip
    // when there is nothing to clean (this only runs once, on init).
    if (!currentHtml.includes('fr-page-break') && !currentHtml.includes('Page Break')) return;

    const cleanedHtml = removePageBreaks(currentHtml);
    if (cleanedHtml === currentHtml) return;

    const marker = editor.markers.insert();
    editor.html.set(cleanedHtml);
    if (marker) editor.markers.remove();
    this.editor().patchValue(cleanedHtml, { emitEvent: false });
  }
}
